import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Megaphone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const buildUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return `${API}${u}`;
};

// HTML promos render inside an isolated iframe (srcDoc) so the client's
// standalone HTML/CSS/JS cannot leak into or break the main site.
// The iframe is made transparent so the client's own overlay dims the real
// site behind the popup (no opaque blank screen).
const TRANSPARENT_STYLE =
  '<style>html,body{background:transparent!important;background-color:transparent!important;}</style>';

// Bridge: when the client's own popup closes (their × / "Non merci" hides the
// overlay) or Escape is pressed inside the iframe, tell the parent to unmount
// the whole iframe layer so the site becomes accessible again. This lets us use
// a SINGLE close control (the client's own), avoiding a confusing second cross.
const BRIDGE_SCRIPT =
  "<script>(function(){function c(){try{parent.postMessage('barnum-promo-close','*');}catch(e){}}" +
  "function h(el){if(!el)return false;var s=getComputedStyle(el);return s.display==='none'||s.visibility==='hidden'||el.offsetParent===null;}" +
  "function init(){var ov=document.getElementById('ov')||document.querySelector('.overlay');" +
  "if(ov){new MutationObserver(function(){if(h(ov))c();}).observe(ov,{attributes:true,attributeFilter:['style','class','hidden']});}" +
  "document.addEventListener('keydown',function(e){if(e.key==='Escape')c();});}" +
  "if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',init);}else{init();}})();<\/script>";

const PromoHtmlModal = ({ promo }) => (
  <div className="fixed inset-0 z-[100]" data-testid="promo-html-modal">
    <iframe
      title={promo.promoTitle || 'Promotion'}
      srcDoc={promo.promoHtml + TRANSPARENT_STYLE + BRIDGE_SCRIPT}
      className="ph-no-capture w-full h-full border-0"
      style={{ background: 'transparent' }}
      data-testid="promo-html"
    />
  </div>
);

const PromoMediaModal = ({ promo, onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    onClick={onClose}
    data-testid="promo-modal-overlay"
  >
    <div
      className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
      data-testid="promo-modal"
    >
      <button
        onClick={onClose}
        className="absolute top-3 right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 shadow-md text-gray-700 hover:bg-orange-500 hover:text-white transition-colors"
        data-testid="promo-modal-close"
        aria-label="Fermer"
      >
        <X className="w-5 h-5" />
      </button>
      <div className="p-4 sm:p-6">
        {promo.promoTitle && (
          <h3 className="text-xl font-bold text-gray-900 mb-4 pr-8" data-testid="promo-modal-title">
            {promo.promoTitle}
          </h3>
        )}
        {promo.promoType === 'image' && promo.promoImageUrl && (
          <img
            src={buildUrl(promo.promoImageUrl)}
            alt={promo.promoTitle || 'Promotion'}
            className="w-full h-auto rounded-lg"
            data-testid="promo-image"
          />
        )}
        {promo.promoType === 'pdf' && promo.promoPdfUrl && (
          <iframe
            src={buildUrl(promo.promoPdfUrl)}
            title={promo.promoTitle || 'Promotion PDF'}
            className="ph-no-capture w-full rounded-lg border border-gray-200"
            style={{ height: '70vh' }}
            data-testid="promo-pdf"
          />
        )}
      </div>
    </div>
  </div>
);

const PromoModal = ({ promo, onClose }) => {
  if (promo.promoType === 'html') return <PromoHtmlModal promo={promo} />;
  return <PromoMediaModal promo={promo} onClose={onClose} />;
};

const PromoDisplay = () => {
  const location = useLocation();
  const [promo, setPromo] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => setPromo(data))
      .catch(() => {});
  }, []);

  const hasContent =
    promo &&
    ((promo.promoType === 'image' && promo.promoImageUrl) ||
      (promo.promoType === 'pdf' && promo.promoPdfUrl) ||
      (promo.promoType === 'html' && promo.promoHtml));

  const enabled = promo && promo.promoEnabled && hasContent;
  const mode = promo ? promo.promoDisplayMode || 'both' : 'both';

  // Auto-open popup on every visit / reload
  useEffect(() => {
    if (!enabled) return;
    if (mode !== 'popup' && mode !== 'both') return;
    setModalOpen(true);
  }, [enabled, mode]);

  const closeModal = () => {
    setModalOpen(false);
  };

  // Allow the HTML popup (inside the iframe) to close the whole layer via
  // postMessage, plus Escape from the parent document. Single close control.
  useEffect(() => {
    if (!modalOpen) return undefined;
    const onMsg = (e) => {
      if (e.data === 'barnum-promo-close') setModalOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setModalOpen(false);
    };
    window.addEventListener('message', onMsg);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('message', onMsg);
      window.removeEventListener('keydown', onKey);
    };
  }, [modalOpen]);

  if (!enabled) return null;
  if (location.pathname.startsWith('/admin')) return null;

  const showBanner = (mode === 'banner' || mode === 'both') && bannerVisible;

  return (
    <>
      {showBanner && (
        <div
          className="bg-orange-500 text-white"
          data-testid="promo-banner"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-3 text-center">
            <Megaphone className="w-4 h-4 flex-shrink-0 hidden sm:block" />
            <span className="text-xs sm:text-sm font-medium">
              {promo.promoBannerText || promo.promoTitle || 'Découvrez notre offre du moment !'}
            </span>
            <button
              onClick={() => setModalOpen(true)}
              className="flex-shrink-0 text-xs sm:text-sm font-semibold underline underline-offset-2 hover:text-orange-100 transition-colors"
              data-testid="promo-banner-open"
            >
              Découvrir
            </button>
            <button
              onClick={() => setBannerVisible(false)}
              className="flex-shrink-0 ml-1 text-white/80 hover:text-white transition-colors"
              data-testid="promo-banner-close"
              aria-label="Fermer le bandeau"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {modalOpen && <PromoModal promo={promo} onClose={closeModal} />}
    </>
  );
};

export default PromoDisplay;
